import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import dayjs from "dayjs";
import {
  contarComentarios,
  Demanda,
  formatarData,
  isVencida,
  nomeAbreviado,
  SortDirection,
  SortField,
  STATUS_BY_KEY,
} from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";
import { StatusPill, UserAvatar } from "./shared";

const LOTE = 20;

const COLUNAS: { campo: SortField; label: string; largura?: number }[] = [
  { campo: "codigo", label: "Código", largura: 130 },
  { campo: "titulo", label: "Título" },
  { campo: "responsavel", label: "Responsável", largura: 190 },
  { campo: "status", label: "Status", largura: 180 },
  { campo: "dataPrevista", label: "Data prevista", largura: 150 },
  { campo: "dataCriacao", label: "Data de criação", largura: 160 },
];

function valorPara(demanda: Demanda, campo: SortField): string | number {
  switch (campo) {
    case "status":
      return STATUS_BY_KEY[demanda.status]?.label ?? "";
    case "dataPrevista":
    case "dataCriacao": {
      const valor = demanda[campo];
      if (!valor) return Number.POSITIVE_INFINITY;
      const data = dayjs(valor);
      return data.isValid() ? data.valueOf() : Number.POSITIVE_INFINITY;
    }
    default:
      return (demanda[campo] ?? "").toLocaleLowerCase("pt-BR");
  }
}

function ordenar(
  demandas: Demanda[],
  campo: SortField,
  direcao: SortDirection,
): Demanda[] {
  const sinal = direcao === "asc" ? 1 : -1;
  return [...demandas].sort((a, b) => {
    const va = valorPara(a, campo);
    const vb = valorPara(b, campo);
    if (va === vb) return a.codigo.localeCompare(b.codigo) * sinal;
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * sinal;
    }
    return String(va).localeCompare(String(vb), "pt-BR") * sinal;
  });
}

function BotaoArquivar({
  demanda,
  ocupado,
  onArquivar,
  className,
}: {
  demanda: Demanda;
  ocupado: boolean;
  onArquivar: (demanda: Demanda) => void;
  className?: string;
}) {
  const theme = useTheme();
  const styles = styless(theme);
  const restaurar = !!demanda.arquivada;

  return (
    <Tooltip title={restaurar ? "Restaurar" : "Arquivar"} arrow>
      <Box component="span" className={className}>
        <IconButton
          size="small"
          disabled={ocupado}
          sx={styles.botaoArquivar}
          aria-label={restaurar ? "Restaurar chamado" : "Arquivar chamado"}
          onClick={(evento) => {
            evento.stopPropagation();
            onArquivar(demanda);
          }}
        >
          {ocupado ? (
            <CircularProgress size={15} thickness={5} />
          ) : restaurar ? (
            <UnarchiveOutlinedIcon sx={{ fontSize: 18 }} />
          ) : (
            <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

const Linha = memo(function Linha({
  demanda,
  indice,
  onAbrir,
  onArquivar,
  arquivando,
}: {
  demanda: Demanda;
  indice: number;
  onAbrir: (demanda: Demanda) => void;
  onArquivar?: (demanda: Demanda) => void;
  arquivando: boolean;
}) {
  const theme = useTheme();
  const styles = styless(theme);
  const vencida = isVencida(demanda);
  const comentarios = contarComentarios(demanda);

  return (
    <TableRow
      hover={false}
      sx={styles.linha(vencida)}
      style={{ ["--i" as string]: indice }}
      onClick={() => onAbrir(demanda)}
    >
      <TableCell sx={styles.celula}>
        <Box component="span" sx={styles.codigo}>
          {demanda.codigo}
        </Box>
      </TableCell>
      <TableCell sx={styles.celula}>
        <Box sx={styles.tituloComBadge}>
          <Tooltip title={demanda.titulo} arrow enterDelay={600}>
            <Typography sx={styles.titulo}>{demanda.titulo}</Typography>
          </Tooltip>
          {demanda.pendenteSync && (
            <Tooltip
              arrow
              title={
                demanda.descartado
                  ? `Recusado. ${demanda.erroSync ?? ""}`
                  : "Será enviado automaticamente."
              }
            >
              <Chip
                size="small"
                icon={<CloudOffOutlinedIcon sx={{ fontSize: 14 }} />}
                label={demanda.descartado ? "Não enviado" : "Aguardando envio"}
                color={demanda.descartado ? "error" : "warning"}
                variant="outlined"
              />
            </Tooltip>
          )}
          {comentarios > 0 && (
            <Box component="span" sx={styles.comentariosBadge}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
              {comentarios}
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell sx={styles.celula}>
        {demanda.responsavel ? (
          <Box sx={styles.responsavel}>
            <UserAvatar
              nome={demanda.responsavel}
              foto={demanda.responsavelFoto}
              tamanho={26}
            />
            <Tooltip title={demanda.responsavel} arrow enterDelay={600}>
              <Typography sx={{ fontSize: "0.85rem" }}>
                {nomeAbreviado(demanda.responsavel)}
              </Typography>
            </Tooltip>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.85rem", color: "text.disabled" }}>
            A definir
          </Typography>
        )}
      </TableCell>
      <TableCell sx={styles.celula}>
        <StatusPill status={demanda.status} />
      </TableCell>
      <TableCell sx={styles.celula}>
        {vencida ? (
          <Tooltip title="Data prevista no passado" arrow>
            <Box component="span" sx={styles.dataVencida}>
              <EventBusyOutlinedIcon sx={{ fontSize: 15 }} />
              {formatarData(demanda.dataPrevista)}
            </Box>
          </Tooltip>
        ) : (
          formatarData(demanda.dataPrevista)
        )}
      </TableCell>
      <TableCell sx={styles.celula}>
        {formatarData(demanda.dataCriacao)}
      </TableCell>
      <TableCell sx={styles.celula} padding="none">
        <Box sx={styles.acoesLinha}>
          {onArquivar && !demanda.pendenteSync && (
            <BotaoArquivar
              className="linha-acao"
              demanda={demanda}
              ocupado={arquivando}
              onArquivar={onArquivar}
            />
          )}
          <Box className="linha-seta" sx={styles.setaLinha}>
            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
});

const Card = memo(function Card({
  demanda,
  indice,
  onAbrir,
  onArquivar,
  arquivando,
}: {
  demanda: Demanda;
  indice: number;
  onAbrir: (demanda: Demanda) => void;
  onArquivar?: (demanda: Demanda) => void;
  arquivando: boolean;
}) {
  const theme = useTheme();
  const styles = styless(theme);
  const vencida = isVencida(demanda);
  const meta = STATUS_BY_KEY[demanda.status];
  const cor = vencida
    ? theme.palette.error.main
    : theme.palette.chip[meta?.chipKey ?? "arquive"];
  const comentarios = contarComentarios(demanda);

  return (
    <Paper
      sx={styles.card(cor)}
      style={{ ["--i" as string]: indice }}
      onClick={() => onAbrir(demanda)}
    >
      <Box sx={styles.cardTopo}>
        <Box component="span" sx={styles.codigo}>
          {demanda.codigo}
        </Box>
        <Box sx={{ ...styles.acoesLinha, gap: 0.75 }}>
          <StatusPill status={demanda.status} tamanho="sm" />
          {onArquivar && !demanda.pendenteSync && (
            <BotaoArquivar
              demanda={demanda}
              ocupado={arquivando}
              onArquivar={onArquivar}
            />
          )}
        </Box>
      </Box>
      <Typography sx={styles.cardTitulo}>{demanda.titulo}</Typography>
      <Box sx={styles.cardRodape}>
        {demanda.responsavel && (
          <Box sx={styles.responsavel}>
            <UserAvatar
              nome={demanda.responsavel}
              foto={demanda.responsavelFoto}
              tamanho={22}
            />
            {nomeAbreviado(demanda.responsavel)}
          </Box>
        )}
        <Box component="span" sx={vencida ? styles.dataVencida : undefined}>
          Prevista {formatarData(demanda.dataPrevista)}
        </Box>
        <Box component="span">Aberta {formatarData(demanda.dataCriacao)}</Box>
        {comentarios > 0 && (
          <Box component="span" sx={styles.comentariosBadge}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
            {comentarios}
          </Box>
        )}
      </Box>
    </Paper>
  );
});

interface DemandasTableProps {
  demandas: Demanda[];
  loading: boolean;
  resetKey: string;
  onAbrir: (demanda: Demanda) => void;
  onArquivar?: (demanda: Demanda) => void;
  arquivandoId?: string | null;
}

export default function DemandasTable({
  demandas,
  loading,
  resetKey,
  onAbrir,
  onArquivar,
  arquivandoId = null,
}: DemandasTableProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const compacto = useMediaQuery(theme.breakpoints.down("mdl"));

  const [campo, setCampo] = useState<SortField>("dataCriacao");
  const [direcao, setDirecao] = useState<SortDirection>("desc");
  const [limite, setLimite] = useState(LOTE);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLimite(LOTE);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [resetKey, campo, direcao]);

  const ordenadas = useMemo(
    () => ordenar(demandas, campo, direcao),
    [demandas, campo, direcao],
  );

  const visiveis = useMemo(
    () => ordenadas.slice(0, limite),
    [ordenadas, limite],
  );
  const restantes = ordenadas.length - visiveis.length;

  useEffect(() => {
    const alvo = sentinelaRef.current;
    if (!alvo || restantes <= 0) return;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) {
          setLimite((atual) => atual + LOTE);
        }
      },
      { root: scrollRef.current, rootMargin: "160px" },
    );
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [restantes, visiveis.length]);

  const alternar = (proximo: SortField) => {
    if (proximo === campo) {
      setDirecao((atual) => (atual === "asc" ? "desc" : "asc"));
    } else {
      setCampo(proximo);
      setDirecao(proximo === "dataCriacao" ? "desc" : "asc");
    }
  };

  if (loading) {
    return (
      <Paper sx={styles.tabelaPaper}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={styles.skeletonLinha}>
            <Skeleton variant="rounded" animation="wave" width={92} height={22} />
            <Skeleton variant="text" animation="wave" sx={{ flex: 1 }} />
            <Skeleton variant="circular" animation="wave" width={26} height={26} />
            <Skeleton variant="rounded" animation="wave" width={120} height={24} />
            <Skeleton variant="text" animation="wave" width={80} />
          </Box>
        ))}
      </Paper>
    );
  }

  const rodape = restantes > 0 && (
    <Box sx={styles.rodapeTabela}>
      <CircularProgress size={13} thickness={5} />
      Carregando mais…
    </Box>
  );

  if (compacto) {
    return (
      <Box ref={scrollRef} sx={styles.cardsScroll}>
        <Box sx={styles.cardsLista}>
          {visiveis.map((demanda, i) => (
            <Card
              key={demanda.id}
              demanda={demanda}
              indice={i % LOTE}
              onAbrir={onAbrir}
              onArquivar={onArquivar}
              arquivando={arquivandoId === demanda.id}
            />
          ))}
        </Box>
        <Box ref={sentinelaRef} />
        {rodape}
      </Box>
    );
  }

  return (
    <Paper sx={styles.tabelaPaper}>
      <Box ref={scrollRef} sx={styles.tabelaScroll}>
        <Table sx={styles.tabela} size="small" stickyHeader>
          <TableHead sx={styles.thead}>
            <TableRow>
              {COLUNAS.map((coluna) => (
                <TableCell
                  key={coluna.campo}
                  width={coluna.largura}
                  sortDirection={campo === coluna.campo ? direcao : false}
                >
                  <TableSortLabel
                    active={campo === coluna.campo}
                    direction={campo === coluna.campo ? direcao : "asc"}
                    onClick={() => alternar(coluna.campo)}
                  >
                    {coluna.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell width={onArquivar ? 80 : 36} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visiveis.map((demanda, i) => (
              <Linha
                key={demanda.id}
                demanda={demanda}
                indice={i % LOTE}
                onAbrir={onAbrir}
                onArquivar={onArquivar}
                arquivando={arquivandoId === demanda.id}
              />
            ))}
          </TableBody>
        </Table>
        <Box ref={sentinelaRef} />
      </Box>
      {rodape}
    </Paper>
  );
}
